import type { GridColDef } from '@mui/x-data-grid';
import type { GetContractsOverviewsResponse, GetContractsResponse } from '@vy/api-definition';
import { CONTRACT_FILTERABLE_FIELDS } from '@vy/contracts-types';
import { CONTRACT_STATUSES, CURRENCIES, DEFAULT_CURRENCY, INTEGRATION_PROVIDERS, SYNC_STATUSES, Plan } from '@vy/types';
import { conditionalWithDefault, isNotNullOrUndefined, isNullOrUndefined, vyDate, withDefault, type ItemOf } from '@vy/utils';
import { convertCurrencyToUsd, findIntegrationByProviderInEntities, formatPrice } from '@vy/services';
import type { FieldPath } from 'react-hook-form';
import { get } from 'react-hook-form';
import { Skeleton, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { ContractStatusChip } from '../../../../components/design/ContractStatusChip';
import { calculateEstimatedPlanValue } from '../../../../utils/contract/calculateEstimatedPlanValue';
import { formatDate } from '../../../../utils/date';
import { InvoiceSyncStatusLabel } from '../../../invoices/InvoicesPage/invoices-page-components/InvoiceSyncStatusLabel/InvoiceSyncStatusLabel';
import { getPlanDurationLabel } from '../../utils';
import { ContractDotMenu } from '../plans-page-components/ContractDotMenu';
import { isPendingExtractionRow } from '../plans-page-utils/pending-extraction-contract-row';
import { ContractLabelsCell } from '../plans-page-components/ContractLabelsCell';
import { useExchangeRates } from '../../../../hooks/requests/exchange-rates/use-exchange-rates';

type ContractRow = ItemOf<GetContractsResponse['contracts']>;

export const CONTRACTS_COLUMNS_KEYS = Object.freeze({
  ID: 'id',
  STATUS: 'status',
  CUSTOMER_NAME: 'customer.name',
  LABELS: 'labels',
  REVENUE: 'revenue',
  PLAN_NAME: 'plan.name',
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  PLAN_DURATION: 'plan.billingData.duration',
  PLAN_TYPE: 'plan.billingData.paymentTerm',
  CREATED_AT: 'createdAt',
  SYNC_STATUS: 'integrationEntities.0.syncStatus',
  BILLING_INTERVAL: 'plan.billingData.billingInterval',
  CURRENCY: 'customer.settings.currency',
  CUSTOMER_ID: 'customer.id',
  SIGNATURE_DATE: 'signatureDate',
  AUTO_RENEWAL: 'plan.billingData.autoRenewal',
  ACTION_MENU: 'actionMenu',
} satisfies Record<string, FieldPath<ItemOf<GetContractsResponse['contracts']>> | 'actionMenu' | 'labels'>);

const PENDING_SKELETON_WIDTH_DEFAULT = '80%';

function renderWithPendingFallback<TRow extends ContractRow>(
  renderFn: (row: TRow) => React.ReactNode,
  width: string = PENDING_SKELETON_WIDTH_DEFAULT,
) {
  return (row: TRow) => {
    if (isPendingExtractionRow(row)) {
      return <Skeleton variant="text" width={width} />;
    }
    return renderFn(row);
  };
}

const EstimationCell: React.FC<{ contract: ContractRow }> = ({ contract }) => {
  const { data: exchangeRates } = useExchangeRates();
  const currency = contract.customer.settings?.currency ?? DEFAULT_CURRENCY;
  const estimatedPrice = calculateEstimatedPlanValue(contract.plan as unknown as Plan);

  const usdEstimation = useMemo(() => {
    if (isNullOrUndefined(exchangeRates)) return 0;

    return convertCurrencyToUsd(estimatedPrice, currency, exchangeRates);
  }, [estimatedPrice, currency, exchangeRates]);

  return <span className="font-sans text-black">{formatPrice(usdEstimation, CURRENCIES.Usd)}</span>;
};

export const CONTRACTS_COLUMNS: GridColDef<ContractRow>[] = [
  {
    field: CONTRACTS_COLUMNS_KEYS.STATUS,
    headerName: 'Status',
    headerClassName: 'pl-7',
    cellClassName: 'pl-6',
    minWidth: 140,
    renderCell: ({ row }) => {
      if (isPendingExtractionRow(row)) {
        return (
          <Typography
            sx={{
              fontSize: 13,
              color: "text.secondary",
              fontStyle: 'italic'
            }}>Extracting {row.__filename}...
                      </Typography>
        );
      }

      return <ContractStatusChip status={row.status} endDate={row.endDate} />;
    },
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.CUSTOMER_NAME,
    valueGetter: (_value, row, column) => get(row, column.field),
    headerName: 'Customer',
    minWidth: 200,
    renderCell: ({ row, value }) =>
      renderWithPendingFallback((contract: ContractRow) => (
        <span className="font-sans text-black truncate max-w-[200px]" title={value}>
          {get(contract, CONTRACTS_COLUMNS_KEYS.CUSTOMER_NAME)}
        </span>
      ))(row),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.LABELS,
    headerName: 'Labels',
    minWidth: 200,
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => <ContractLabelsCell contract={row} />,
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.CUSTOMER_ID,
    headerName: 'Customer ID',
    headerClassName: 'pl-7',
    cellClassName: 'pl-6',
    minWidth: 240,
    renderCell: ({ row }) =>
      renderWithPendingFallback((contract: ContractRow) => (
        <span className="font-sans text-black">{contract.customer.id}</span>
      ))(row),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.PLAN_NAME,
    valueGetter: (_value, row, column) => get(row, column.field),
    headerName: 'Contract',
    minWidth: 200,
    flex: 1,
    renderCell: ({ row, value }) =>
      renderWithPendingFallback((_contract: ContractRow) => value)(row),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.START_DATE,
    valueGetter: (_value, row, column) => get(row, column.field),
    headerName: 'Start date',
    minWidth: 140,
    renderCell: ({ row }) =>
      renderWithPendingFallback((contract: ContractRow) => {
        const startDate = conditionalWithDefault(contract.initialStartDate, contract.startDate, () =>
          isNotNullOrUndefined(contract.revisedAt),
        );
        return <span className="font-sans text-black">{formatDate(startDate)}</span>;
      })(row),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.AUTO_RENEWAL,
    headerName: 'Auto renewal',
    minWidth: 140,
    renderCell: ({ row }) =>
      renderWithPendingFallback((contract: ContractRow) => (
        <span className="font-sans text-black">{contract.plan.billingData.autoRenewal ? 'Yes' : 'No'}</span>
      ))(row),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.END_DATE,
    valueGetter: (_value, row, column) => get(row, column.field),
    headerName: 'End date',
    minWidth: 140,
    renderCell: ({ row }) =>
      renderWithPendingFallback((contract: ContractRow) => {
        const endDate = isNotNullOrUndefined(contract.endDate) ? formatDate(contract.endDate) : '--';
        return <span className="font-sans text-black">{endDate}</span>;
      })(row),
  },
  {
    field: 'daysToEnd',
    valueGetter: (_value, row, column) => get(row, column.field),
    headerName: 'Days to end',
    minWidth: 130,
    sortable: false,
    renderCell: ({ row }) =>
      renderWithPendingFallback((contract: ContractRow) => (
        <span className="font-sans text-black">{getContractTimeToEnd(contract)}</span>
      ))(row),
  },
  {
    field: 'contractEstimation',
    valueGetter: (_value, row, column) => get(row, column.field),
    headerName: 'Estimation',
    minWidth: 140,
    sortable: false,
    renderCell: ({ row }) =>
      renderWithPendingFallback((contract: ContractRow) => <EstimationCell contract={contract} />)(row),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.SIGNATURE_DATE,
    valueGetter: (_value, row, column) => get(row, column.field),
    headerName: 'Signature date',
    minWidth: 150,
    renderCell: ({ row, value }) =>
      renderWithPendingFallback((_contract: ContractRow) => {
        const date = isNotNullOrUndefined(value) ? formatDate(value) : '--';
        return <span className="font-sans text-black">{date}</span>;
      })(row),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.BILLING_INTERVAL,
    headerName: 'Billing Interval',
    minWidth: 150,
    renderCell: ({ row }) =>
      renderWithPendingFallback((contract: ContractRow) => (
        <span className="font-sans text-black">{contract.plan.billingData.billingInterval}</span>
      ))(row),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.CREATED_AT,
    valueGetter: (_value, row, column) => get(row, column.field),
    headerName: 'Created Date',
    minWidth: 140,
    renderCell: ({ row, value }) =>
      renderWithPendingFallback((_contract: ContractRow) => (
        <span className="font-sans text-black">{formatDate(value)}</span>
      ))(row),
  },
  {
    field: CONTRACT_FILTERABLE_FIELDS.UPDATED_AT,
    valueGetter: (_value, row, column) => get(row, column.field),
    headerName: 'Updated Date',
    minWidth: 140,
    renderCell: ({ row, value }) =>
      renderWithPendingFallback((_contract: ContractRow) => (
        <span className="font-sans text-black">{formatDate(value)}</span>
      ))(row),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.PLAN_DURATION,
    valueGetter: (_value, row, column) => get(row, column.field),
    headerName: 'Duration',
    minWidth: 130,
    renderCell: ({ row, value }) =>
      renderWithPendingFallback((_contract: ContractRow) => (
        <span className="font-sans text-black">{getPlanDurationLabel(value)}</span>
      ))(row),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.SYNC_STATUS,
    headerName: 'SO Sync',
    headerAlign: 'center',
    minWidth: 130,
    sortable: false,
    renderCell: ({ row }) => {
      if (isPendingExtractionRow(row)) {
        return <Skeleton variant="text" width={PENDING_SKELETON_WIDTH_DEFAULT} />;
      }

      const netSuiteIntegrationEntity = findIntegrationByProviderInEntities(
        row.integrationEntities ?? [],
        INTEGRATION_PROVIDERS.netSuite,
      );
      const syncStatus = withDefault(netSuiteIntegrationEntity?.syncStatus, SYNC_STATUSES.None);
      const syncStatusUpdatedAt = netSuiteIntegrationEntity?.updatedAt;

      if (isNotNullOrUndefined(syncStatus) && isNotNullOrUndefined(syncStatusUpdatedAt)) {
        const syncStatusUpdatedAtDate = formatDate(vyDate(syncStatusUpdatedAt), 'MMM. D, YY HH:mm z');

        return (
          <InvoiceSyncStatusLabel
            className="flex w-full items-center justify-center"
            syncStatus={syncStatus}
            syncStatusUpdatedAt={syncStatusUpdatedAtDate}
          />
        );
      }

      return <span />;
    },
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.CURRENCY,
    headerName: 'Currency',
    headerClassName: 'pl-7',
    cellClassName: 'pl-6',
    minWidth: 120,
    renderCell: ({ row }) =>
      renderWithPendingFallback((contract: ContractRow) => (
        <span className="font-sans text-black">{contract.customer.settings?.currency}</span>
      ))(row),
  },
  {
    field: 'actionMenu',
    headerName: '',
    sortable: false,
    filterable: false,
    width: 30,
    headerClassName: 'bg-white',
    renderCell: ({ row: contract }) => {
      if (isPendingExtractionRow(contract)) {
        return null;
      }

      const netSuiteIntegrationEntity = findIntegrationByProviderInEntities(
        contract.integrationEntities ?? [],
        INTEGRATION_PROVIDERS.netSuite,
      );
      const syncStatus = withDefault(netSuiteIntegrationEntity?.syncStatus, SYNC_STATUSES.None);

      return (
        <ContractDotMenu
          isEditable={contract.isEditable}
          contractId={contract.id}
          planId={contract.plan.id}
          customerId={contract.customer.id}
          planName={contract.plan.name}
          billingData={contract.plan.billingData}
          customerName={contract.customer.name}
          isActive={contract.status === CONTRACT_STATUSES.Active}
          isTerminated={
            contract.status === CONTRACT_STATUSES.Terminated ||
            contract.status === CONTRACT_STATUSES.PendingTermination
          }
          contractStartDate={contract.startDate}
          contractEndDate={contract.endDate}
          syncStatus={syncStatus}
        />
      );
    },
  },
];

function getContractTimeToEnd(contract: ItemOf<GetContractsOverviewsResponse['contracts']>) {
  const today = vyDate();
  const endDate = vyDate(contract.endDate);

  return endDate.diff(today, 'days');
}
